/** Merge gateway research jobs into the local Queue tab without dropping in-flight submits. */

export const LOCAL_QUEUE_GRACE_MS = 90_000;

export type ResearchQueueSyncStatus = 'queued' | 'screening' | 'completed' | 'failed' | 'cancelled';

export type ResearchQueueSyncItem = {
  id: string;
  reportName: string;
  targetType: 'company' | 'person' | 'unknown';
  targetName: string;
  website?: string;
  status: ResearchQueueSyncStatus;
  progress: number;
  message: string;
  createdAt: string;
  updatedAt: string;
  runStartedAt?: string | null;
  etaSeconds?: number;
  reportId?: string;
  gatewayJobId?: string;
};

export type GatewayQueueJob = {
  job_id: string;
  status?: string;
  phase?: string;
  progress_percent?: number;
  target_type?: string;
  target_name?: string;
  created_at?: string;
  updated_at?: string;
  started_at?: string | null;
  current_check?: string;
  current_check_elapsed_ms?: number;
  checks_completed?: number;
  checks_total?: number;
  vantage_job_id?: string;
};

export const normalizeResearchTargetKey = (name: string): string => (
  String(name || '').trim().toLowerCase().replace(/\s+/g, ' ')
);

export const mapGatewayJobToQueueStatus = (status: string | undefined): ResearchQueueSyncStatus => {
  const normalized = String(status || '').trim().toLowerCase();
  if (normalized === 'queued') return 'queued';
  if (normalized === 'running') return 'screening';
  if (normalized === 'completed') return 'completed';
  if (normalized === 'cancelled') return 'cancelled';
  return 'failed';
};

const isActiveQueueStatus = (status: string | undefined): boolean => (
  ['queued', 'screening'].includes(String(status || '').toLowerCase())
);

const isTerminalGatewayStatus = (status: string | undefined): boolean => (
  ['completed', 'failed', 'cancelled', 'interrupted'].includes(String(status || '').trim().toLowerCase())
);

const isActiveGatewayStatus = (status: string | undefined): boolean => {
  const normalized = String(status || '').trim().toLowerCase();
  return normalized === 'queued' || normalized === 'running';
};

const FAILED_QUEUE_LOOKBACK_MS = 48 * 60 * 60 * 1000;

const jobMatchesDismissedIds = (
  job: GatewayQueueJob,
  dismissedIds?: Iterable<string>,
): boolean => {
  if (!dismissedIds) {
    return false;
  }
  const dismissed = dismissedIds instanceof Set ? dismissedIds : new Set(Array.from(dismissedIds));
  if (dismissed.size === 0) {
    return false;
  }
  const gatewayJobId = String(job.job_id || '').trim();
  const vantageJobId = String(job.vantage_job_id || '').trim();
  const candidates = [
    gatewayJobId,
    vantageJobId,
    gatewayJobId ? `report-${gatewayJobId}` : '',
    vantageJobId ? `report-v-${vantageJobId}` : '',
    vantageJobId ? `report-${vantageJobId}` : '',
  ];
  return candidates.some((id) => id && dismissed.has(id));
};

export function shouldKeepLocalActiveQueueItem(
  item: Pick<ResearchQueueSyncItem, 'status' | 'gatewayJobId' | 'createdAt'>,
  serverById: Map<string, GatewayQueueJob>,
  nowMs: number,
  graceMs: number = LOCAL_QUEUE_GRACE_MS,
): boolean {
  if (!isActiveQueueStatus(item.status)) {
    return true;
  }
  const gatewayJobId = String(item.gatewayJobId || '').trim();
  if (!gatewayJobId) {
    // POST /async has not returned yet — never drop the optimistic row.
    return true;
  }
  const server = serverById.get(gatewayJobId);
  if (server) {
    if (isActiveGatewayStatus(server.status)) {
      return true;
    }
    if (isTerminalGatewayStatus(server.status)) {
      const mapped = mapGatewayJobToQueueStatus(server.status);
      return mapped === 'failed' || mapped === 'cancelled';
    }
    return true;
  }
  const created = Date.parse(item.createdAt);
  if (Number.isFinite(created) && nowMs - created < graceMs) {
    return true;
  }
  return false;
}

export function mergeResearchQueueFromGatewayJobs(args: {
  current: ResearchQueueSyncItem[];
  serverJobs: GatewayQueueJob[];
  language: 'en' | 'es';
  nowMs?: number;
  dismissedIds?: Iterable<string>;
  buildMessage: (job: GatewayQueueJob) => string;
}): { next: ResearchQueueSyncItem[]; pollsToStart: ResearchQueueSyncItem[] } {
  const nowMs = args.nowMs ?? Date.now();
  const serverJobs = (args.serverJobs || []).filter((job) => job && typeof job.job_id === 'string');
  const activeServerJobs = serverJobs.filter((job) => (
    isActiveGatewayStatus(job.status) && !jobMatchesDismissedIds(job, args.dismissedIds)
  ));
  const serverById = new Map(
    serverJobs.map((job) => [String(job.job_id || '').trim(), job] as const)
      .filter((entry) => entry[0]),
  );
  const activeTargetKeys = new Set(
    activeServerJobs.map((job) => normalizeResearchTargetKey(job.target_name || '')),
  );
  const pollsToStart: ResearchQueueSyncItem[] = [];

  let next = args.current.filter((item) => {
    if (item.status !== 'cancelled') {
      return true;
    }
    const targetKey = normalizeResearchTargetKey(item.targetName);
    return !activeTargetKeys.has(targetKey);
  });

  const applyServerJob = (job: GatewayQueueJob, startPoll: boolean): void => {
    const gatewayJobId = String(job.job_id || '').trim();
    if (!gatewayJobId) {
      return;
    }
    const status = mapGatewayJobToQueueStatus(job.status);
    const progress = typeof job.progress_percent === 'number'
      ? Math.max(0, Math.min(100, Math.round(job.progress_percent)))
      : 0;
    const message = args.buildMessage(job);
    const jobTargetKey = normalizeResearchTargetKey(job.target_name || '');
    let existingIndex = next.findIndex((item) => item.gatewayJobId === gatewayJobId);
    if (existingIndex < 0 && jobTargetKey && startPoll) {
      existingIndex = next.findIndex((item) => (
        item.status === 'failed'
        && normalizeResearchTargetKey(item.targetName) === jobTargetKey
      ));
    }

    if (existingIndex >= 0) {
      const existing = next[existingIndex];
      const updated: ResearchQueueSyncItem = {
        ...existing,
        status,
        progress,
        message,
        gatewayJobId: existing.gatewayJobId || gatewayJobId,
        reportId: existing.reportId || `report-${gatewayJobId}`,
        updatedAt: job.updated_at || existing.updatedAt,
        runStartedAt: job.started_at || existing.runStartedAt || null,
      };
      next = next.map((item, index) => (index === existingIndex ? updated : item));
      if (startPoll) {
        pollsToStart.push(updated);
      }
      return;
    }

    const reportId = `report-${gatewayJobId}`;
    const syncedItem: ResearchQueueSyncItem = {
      id: `queue-sync-${gatewayJobId}`,
      reportName: String(job.target_name || '').trim()
        || (args.language === 'es' ? 'Reporte de investigación' : 'Research report'),
      targetType: job.target_type === 'company' ? 'company' : 'person',
      targetName: String(job.target_name || '').trim(),
      status,
      progress,
      message,
      createdAt: job.created_at || new Date(nowMs).toISOString(),
      updatedAt: job.updated_at || new Date(nowMs).toISOString(),
      runStartedAt: job.started_at || null,
      gatewayJobId,
      reportId,
    };
    next = [syncedItem, ...next];
    if (startPoll) {
      pollsToStart.push(syncedItem);
    }
  };

  for (const job of activeServerJobs) {
    applyServerJob(job, true);
  }

  const failedServerJobs = serverJobs.filter((job) => {
    if (jobMatchesDismissedIds(job, args.dismissedIds)) {
      return false;
    }
    const mapped = mapGatewayJobToQueueStatus(job.status);
    if (mapped !== 'failed' && mapped !== 'cancelled') {
      return false;
    }
    const stamp = Date.parse(job.updated_at || job.created_at || '');
    if (!Number.isFinite(stamp)) {
      return true;
    }
    return nowMs - stamp <= FAILED_QUEUE_LOOKBACK_MS;
  });
  for (const job of failedServerJobs) {
    applyServerJob(job, false);
  }

  next = next.filter((item) => shouldKeepLocalActiveQueueItem(item, serverById, nowMs));

  const latestActiveByTarget = new Map<string, ResearchQueueSyncItem>();
  const otherItems: ResearchQueueSyncItem[] = [];
  next.forEach((item) => {
    if (!isActiveQueueStatus(item.status)) {
      otherItems.push(item);
      return;
    }
    const targetKey = normalizeResearchTargetKey(item.targetName);
    const existing = latestActiveByTarget.get(targetKey);
    if (!existing) {
      latestActiveByTarget.set(targetKey, item);
      return;
    }
    const existingProgress = Number(existing.progress || 0);
    const candidateProgress = Number(item.progress || 0);
    const existingUpdated = Date.parse(existing.updatedAt);
    const candidateUpdated = Date.parse(item.updatedAt);
    const keepCandidate = (
      candidateProgress > existingProgress
      || (candidateProgress === existingProgress && candidateUpdated > existingUpdated)
    );
    if (keepCandidate) {
      latestActiveByTarget.set(targetKey, item);
    }
  });
  next = [...otherItems, ...latestActiveByTarget.values()];

  const latestFailedByTarget = new Map<string, ResearchQueueSyncItem>();
  const nonTerminalItems: ResearchQueueSyncItem[] = [];
  next.forEach((item) => {
    if (!['failed', 'cancelled'].includes(item.status)) {
      nonTerminalItems.push(item);
      return;
    }
    const targetKey = normalizeResearchTargetKey(item.targetName);
    const existing = latestFailedByTarget.get(targetKey);
    if (!existing || Date.parse(item.updatedAt) > Date.parse(existing.updatedAt)) {
      latestFailedByTarget.set(targetKey, item);
    }
  });
  next = [...nonTerminalItems, ...latestFailedByTarget.values()];

  return { next, pollsToStart };
}
